/**
 * @fileoverview <SubprocessorList> — structured table of subprocessor
 * entries used by the payday `subprocessors` document.
 *
 * Each entry renders as a self-contained `<article>` (so screen readers
 * announce vendor boundaries) with rows for role, data categories,
 * location, legal entity, and optional reference links.
 *
 * Styling is centralized in `packages/shared-styles/css/utilities/typography.css`
 * + `cards.css` (no co-located CSS, no inline styles, no BEM).
 *
 * The five row LABELS are chrome, worded from the package's `legalContent` namespace (#56,
 * PACKAGE-I18N-SEAT U3) and marked with the language they are written in; each row's VALUE is the
 * registry's English disclosure, rendered as written.
 *
 * @module @puffin-core/legal-content/components/SubprocessorList
 */

import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useLegalContentTranslate } from '../hooks/useLegalContentTranslate.js';

/**
 * The `<dl>` of one subprocessor's rows — module-private, not exported.
 *
 * ⚠️ LEGALLY-REVIEWED DISCLOSURE COPY. This block is a byte-for-byte lift of what
 * used to sit inline: the same five labels, the same order (Role → Data categories
 * → Location → Legal entity → References), the same markup, the same conditional
 * References arm. Nothing here may be reworded, reordered, or lifted into a data
 * array — an array literal would put this text in JS strings, which is exactly how
 * disclosure copy silently rots. The labels' English lives in the `legalContent`
 * catalog byte for byte (PACKAGE-I18N-SEAT U3), where a parity suite and the
 * provenance sidecar hold every locale to the same five; each `<dt>` still names
 * its own leaf, in its own place, so the order cannot drift.
 *
 * The extraction boundary is the `<dl>` because that is what actually fixes BOTH
 * findings at once:
 *   • jsxDepth — the offending chain was `article → dl → Fragment → dd → ul` (5).
 *     Cutting at the `<dl>` leaves the parent at depth 2 and this component at 4.
 *     (Cutting at the `<article>` instead would NOT have worked: the depth-5 chain
 *     would have travelled with it. Depth is element nesting; a component element
 *     counts just like a host element.)
 *   • nestedMaps — `dataCategories.map` and `references.map` used to nest inside
 *     `entries.map`. They now live one level down and are SIBLINGS, not nested.
 *
 * @param {{ entry: import('../registry/types.js').SubprocessorEntry }} props
 * @returns {React.JSX.Element}
 */
function SubprocessorEntryRows({ entry }) {
  const t = useLegalContentTranslate();
  const lang = t('chromeLanguage');
  return (
    <dl className="subprocessor-entry-rows">
      <Fragment>
        <dt className="subprocessor-entry-label" lang={lang}>
          {t('subprocessorRole')}
        </dt>
        <dd className="subprocessor-entry-value">{entry.role}</dd>
      </Fragment>
      <Fragment>
        <dt className="subprocessor-entry-label" lang={lang}>
          {t('subprocessorDataCategories')}
        </dt>
        <dd className="subprocessor-entry-value">
          <ul className="subprocessor-entry-categories">
            {entry.dataCategories.map((category) => (
              <li key={category}>{category}</li>
            ))}
          </ul>
        </dd>
      </Fragment>
      <Fragment>
        <dt className="subprocessor-entry-label" lang={lang}>
          {t('subprocessorLocation')}
        </dt>
        <dd className="subprocessor-entry-value">{entry.location}</dd>
      </Fragment>
      <Fragment>
        <dt className="subprocessor-entry-label" lang={lang}>
          {t('subprocessorLegalEntity')}
        </dt>
        <dd className="subprocessor-entry-value">{entry.legalEntity}</dd>
      </Fragment>
      {entry.references && entry.references.length > 0 ? (
        <Fragment>
          <dt className="subprocessor-entry-label" lang={lang}>
            {t('subprocessorReferences')}
          </dt>
          <dd className="subprocessor-entry-value">
            <ul className="subprocessor-entry-references">
              {entry.references.map((ref) => (
                <li key={ref.url}>
                  <a
                    href={ref.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="legal-cross-link"
                  >
                    {ref.title}
                  </a>
                </li>
              ))}
            </ul>
          </dd>
        </Fragment>
      ) : null}
    </dl>
  );
}

SubprocessorEntryRows.propTypes = {
  entry: PropTypes.shape({
    role: PropTypes.string.isRequired,
    dataCategories: PropTypes.arrayOf(PropTypes.string).isRequired,
    location: PropTypes.string.isRequired,
    legalEntity: PropTypes.string.isRequired,
    references: PropTypes.arrayOf(
      PropTypes.shape({ title: PropTypes.string.isRequired, url: PropTypes.string.isRequired }),
    ),
  }).isRequired,
};

/**
 * @param {{ entries: import('../registry/types.js').SubprocessorEntry[] }} props
 * @returns {React.JSX.Element|null}
 */
export function SubprocessorList({ entries = [] }) {
  if (!Array.isArray(entries) || entries.length === 0) {
    return null;
  }
  return (
    <div className="subprocessor-list">
      {entries.map((entry) => (
        <article
          key={entry.name}
          className="subprocessor-entry"
          aria-labelledby={`subprocessor-${slugify(entry.name)}-name`}
        >
          <h3 id={`subprocessor-${slugify(entry.name)}-name`} className="subprocessor-entry-name">
            {entry.name}
          </h3>
          <SubprocessorEntryRows entry={entry} />
        </article>
      ))}
    </div>
  );
}

function slugify(str) {
  return String(str)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

SubprocessorList.propTypes = {
  entries: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      role: PropTypes.string.isRequired,
      dataCategories: PropTypes.arrayOf(PropTypes.string).isRequired,
      location: PropTypes.string.isRequired,
      legalEntity: PropTypes.string.isRequired,
      references: PropTypes.arrayOf(
        PropTypes.shape({ title: PropTypes.string.isRequired, url: PropTypes.string.isRequired }),
      ),
    }),
  ),
};

export default SubprocessorList;
